import React, { useEffect, useMemo, useRef, useState } from 'react';
import Script from 'next/script';
import { LiraRunAdvScript } from '@sbt-web/adv';
import {
  getDesktopFallbackConfig,
  getMsiteFallbackConfig,
} from '@client/components/Adv/Lira/fallbackConfig/listing';
import ErrorBoundary from '@sbt-web/error-boundary';
import {
  ListingTargetingProps,
  targetingForListing,
} from '../targeting/forListing';
import { useGetVerticalByCategoryId } from './useGetVerticalByCategoryId';
import type { AdItem } from '@sbt-web/network/types';
import { createSkyscraperSizeConstraints } from '@client/components/Adv/Lira/createSkyscraperSizeConstraints';
import { LiraContainerIds } from '@client/components/Adv/Lira/liraContainerIds';

const adtest =
  process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT === 'prod' ? 'off' : 'on';

type Props = ListingTargetingProps & {
  isModalOpened: boolean;
  adItems: AdItem[];
};

const LiraWrapper = ({
  category,
  adType,
  query,
  geo,
  radiusSearch,
  pageIndex,
  filtersLabels,
  includeShippableOnly,
  adItems,
  isModalOpened,
}: Props) => {
  const [clientSideNavigationStarted, setClientSideNavigationStarted] =
    useState(false);
  const previousItemsList = useRef<Array<AdItem>>(null);
  const isFirstRender = useRef(true);
  const vertical = useGetVerticalByCategoryId(category.id);
  const fallbackConfigDesktop = useMemo(
    () => getDesktopFallbackConfig(vertical),
    [vertical]
  );
  const fallbackConfigMsite = useMemo(
    () => getMsiteFallbackConfig(vertical),
    [vertical]
  );
  const queryStringForAdsense = query ?? category.label;

  const targeting = useMemo(
    () =>
      targetingForListing({
        category,
        geo,
        query,
        adType,
        pageIndex,
        radiusSearch,
        filtersLabels,
        includeShippableOnly,
      }),
    [
      adType,
      category,
      filtersLabels,
      geo,
      includeShippableOnly,
      pageIndex,
      query,
      radiusSearch,
    ]
  );

  const constraintsByContainerId = useMemo(
    () => ({
      ...createSkyscraperSizeConstraints(
        LiraContainerIds.listing.skyscraperLeft,
        LiraContainerIds.listing.skyscraperRight
      ),
      [LiraContainerIds.listing.interstitialLazy]: {
        isLazyLoad: true,
      },
      [LiraContainerIds.listing.belowListing]: {
        isLazyLoad: true,
      },
      [LiraContainerIds.listing.zeroResult]: {
        isLazyLoad: true,
      },
    }),
    []
  );

  /**
   * Reload adv when items change: when the items list is different from the previous one
   */
  useEffect(() => {
    const isItemsListChanged =
      previousItemsList.current !== undefined &&
      adItems != previousItemsList.current;
    if (!isFirstRender.current && isItemsListChanged && !isModalOpened) {
      setClientSideNavigationStarted(true);
      // Skin ADV add a background image to the body, we need to remove it. GAM does not remove it.
      window.document.body.style.removeProperty('background-image');
    }
    previousItemsList.current = adItems;
    isFirstRender.current = false;
  }, [adItems, isModalOpened]);

  return (
    <LiraRunAdvScript
      NextJsScriptComponent={Script}
      vertical={vertical}
      pageType="listing"
      env={process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT}
      fallbackHebeConfig={{
        msite: fallbackConfigMsite,
        desktop: fallbackConfigDesktop,
      }}
      adsense={{ adtest, query: queryStringForAdsense }}
      constraintsByContainerId={constraintsByContainerId}
      targeting={targeting}
      clientSideNavigationStarted={clientSideNavigationStarted}
      onAfterReloaded={() => setClientSideNavigationStarted(false)}
    />
  );
};

export const LiraRunAdvScriptWrapperForListing = (props: Props) => {
  return (
    <ErrorBoundary>
      <LiraWrapper {...props} />
    </ErrorBoundary>
  );
};
